import React, { useState } from "react";
import { FaCheck, FaCopy, FaEnvelope, FaEye, FaEyeSlash, FaKey, FaWhatsapp } from "react-icons/fa";
import InfoTip from "./InfoTip";
import { PASSWORD_POLICY_HINT } from "../utils/passwordPolicy";
import "../assets/css/OwnerPasswordField.css";

const UPPER = "ABCDEFGHJKLMNPQRSTUVWXYZ";
const LOWER = "abcdefghijkmnopqrstuvwxyz";
const DIGITS = "23456789";
const SYMBOLS = "@#$%&*!?";

function randomIndex(max) {
  const buf = new Uint32Array(1);
  window.crypto.getRandomValues(buf);
  return buf[0] % max;
}

/** 12 characters with at least one of each class required by the password policy (no look-alike characters). */
export function generatePassword(length = 12) {
  const all = UPPER + LOWER + DIGITS + SYMBOLS;
  const chars = [UPPER, LOWER, DIGITS, SYMBOLS].map((set) => set[randomIndex(set.length)]);
  while (chars.length < length) chars.push(all[randomIndex(all.length)]);
  for (let i = chars.length - 1; i > 0; i -= 1) {
    const j = randomIndex(i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join("");
}

export function credentialsMessage({ name, portalUrl, email, password }) {
  return [
    `Hi${name ? ` ${name}` : ""},`,
    "",
    "Your Cloud Office login details:",
    `Portal: ${portalUrl}`,
    `Login email: ${email}`,
    `Password: ${password}`,
    "",
    "Please change your password after your first sign-in.",
  ].join("\n");
}

/**
 * Owner password (Generate, show/hide) plus a "Share" grid cell with copy / email / WhatsApp,
 * enabled once there is a password. The shared password only works after the tenant form is saved.
 */
function OwnerPasswordField({
  label = "Password",
  required = false,
  value,
  onChange,
  placeholder,
  loginEmail,
  portalUrl,
  recipientName,
  recipientEmail,
  recipientPhone,
  inlineShare = false,
  hint,
  saveNote = "Save the client first so this password is active.",
  missingNote = "Enter the Owner Login Email and a password to share the login.",
}) {
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const password = String(value || "");
  const canShare = Boolean(password && loginEmail);
  const message = canShare
    ? credentialsMessage({ name: recipientName, portalUrl, email: loginEmail, password })
    : "";
  const waDigits = String(recipientPhone || "").replace(/\D/g, "");
  const waHref = `https://wa.me/${waDigits.length >= 10 ? waDigits : ""}?text=${encodeURIComponent(message)}`;
  const mailHref =
    `mailto:${encodeURIComponent(recipientEmail || "")}` +
    `?subject=${encodeURIComponent("Your Cloud Office login")}&body=${encodeURIComponent(message)}`;
  const shareTitle = canShare ? saveNote : missingNote;

  const generate = () => {
    onChange(generatePassword());
    setVisible(true);
    setCopied(false);
  };

  const copy = async () => {
    if (!canShare) return;
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const shareButtons = (
    <>
        <button
          type="button"
          className="owner-password__icon"
          onClick={copy}
          disabled={!canShare}
          title={canShare ? (copied ? "Copied" : `Copy login details. ${shareTitle}`) : shareTitle}
          aria-label="Copy login details"
        >
          {copied ? <FaCheck /> : <FaCopy />}
        </button>
        <a
          className={`owner-password__icon${canShare ? "" : " is-disabled"}`}
          href={canShare ? mailHref : undefined}
          title={canShare ? `Share by email. ${shareTitle}` : shareTitle}
          aria-label="Share login by email"
          aria-disabled={!canShare}
        >
          <FaEnvelope />
        </a>
        <a
          className={`owner-password__icon owner-password__icon--wa${canShare ? "" : " is-disabled"}`}
          href={canShare ? waHref : undefined}
          target="_blank"
          rel="noreferrer"
          title={canShare ? `Share on WhatsApp. ${shareTitle}` : shareTitle}
          aria-label="Share login on WhatsApp"
          aria-disabled={!canShare}
        >
          <FaWhatsapp />
        </a>
    </>
  );

  return (
    <>
    <div className="form-group owner-password">
      <label className={hint ? "owner-password__label" : undefined}>
        {label}
        {required ? <span className="required"> *</span> : null}
        {hint ? <InfoTip label="Password rules">{hint}</InfoTip> : null}
      </label>
      <div className="owner-password__row">
        <input
          type={visible ? "text" : "password"}
          name="portalPassword"
          value={password}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          placeholder={placeholder}
          autoComplete="new-password"
          title={PASSWORD_POLICY_HINT}
        />
        <button
          type="button"
          className="owner-password__icon"
          onClick={() => setVisible((v) => !v)}
          title={visible ? "Hide password" : "Show password"}
          aria-label={visible ? "Hide password" : "Show password"}
        >
          {visible ? <FaEyeSlash /> : <FaEye />}
        </button>
        <button type="button" className="owner-password__generate" onClick={generate}>
          <FaKey /> Generate
        </button>
      </div>
      {inlineShare ? (
        <div className="owner-password__inline-share" title={shareTitle}>
          <span className="owner-password__inline-label">Share</span>
          {shareButtons}
        </div>
      ) : null}
    </div>
    {inlineShare ? null : (
      <div className="form-group owner-password-share">
        <label>Share</label>
        <div className="owner-password__row" title={shareTitle}>
          {shareButtons}
        </div>
      </div>
    )}
    </>
  );
}

export default OwnerPasswordField;
